import * as React from "react";
import Creatable from "react-select/creatable";
import { Link, useHistory, useLocation } from "react-router-dom";
import { format, parse } from "date-fns";
import {
  Address as MemberAddress,
  ApiRequest,
  Contact,
  WHATA_ENDPOINTS,
  deepEqual,
  nullifyObject,
} from "@plinknz/tah-website-elements";
import { AddressFinderInput } from "../../../../components/form/address-finder-input";
import { Address, AddressMeta } from "../../../../components/form/types";
import { UserContext } from "../../../../service/reducers/user";
import * as ROUTES from "../../../../config/router";
import { Loader } from "../../../../components/loader";
import { DatePicker } from "../../../../components/form/datepicker";
import { whataRequest } from "../../../../service/member/whata";
import { detailsReducer } from "../../../../service/reducers/update-details";
import { Form } from "../../../../components/form/form";
import { Button } from "../../../../components/button";
import { fetchUser as fetchUserDefault } from "../../../../utility/load-member";
import { getPhone, PhoneType } from "../../../../utility/phone";

// TODO: This file is quite large, consider putting each form section into it's own file

interface UpdateDetailsProps {
  whata?: ApiRequest;
  fetchUser?: typeof fetchUserDefault;
}

export const UpdateDetails = ({
  whata = whataRequest,
  fetchUser = fetchUserDefault,
}: UpdateDetailsProps) => {
  const location = useLocation();
  const {
    state: { member, loading: isLoading },
    dispatch: userDispatch,
  } = React.useContext(UserContext);

  let addressDisplay: MemberAddress;
  let phoneDisplay: Contact;
  let emailDisplay: Contact;
  let cellDisplay: Contact;
  let addressReadonly = false;
  let phoneReadonly = false;
  let cellReadonly = false;

  if (member?.addressSurrogated?.length) {
    addressDisplay = member?.addressSurrogated[0];
    addressReadonly = true;
  } else if (member?.address?.length) {
    addressDisplay = member?.address[0];
  }

  if (member?.emailSurrogated?.length) {
    emailDisplay = member?.emailSurrogated[0];
  } else if (member?.email?.length) {
    emailDisplay = member?.email[0];
  }

  if (member?.phoneSurrogated?.length) {
    phoneDisplay = getPhone({
      phone: member?.phoneSurrogated,
      type: PhoneType.Home,
    });
    cellDisplay = getPhone({
      phone: member?.phoneSurrogated,
      type: PhoneType.Cell,
    });
  }

  if (phoneDisplay) {
    phoneReadonly = true;
  } else {
    phoneDisplay = getPhone({ phone: member?.phone, type: PhoneType.Home });
  }

  if (cellDisplay) {
    cellReadonly = true;
  } else {
    cellDisplay = getPhone({ phone: member?.phone, type: PhoneType.Cell });
  }

  const [state, formDispatch] = React.useReducer<typeof detailsReducer>(
    detailsReducer,
    {
      title: member?.title || "",
      firstName: member?.firstName,
      middleNames: member?.middleNames || "",
      surname: member?.surname,
      gender: member?.gender || "",
      dob: member?.dob || "",
      email: emailDisplay?.value || "",
      occupation: member?.occupation || "",
      phone: phoneDisplay?.value || "",
      cell: cellDisplay?.value || "",
      address: {
        line1: addressDisplay?.line1 || "",
        line2: addressDisplay?.line2 || "",
        suburb: addressDisplay?.suburb || "",
        city: addressDisplay?.city || "",
        country: addressDisplay?.country || "",
        postcode: addressDisplay?.postcode || "",
      },
    }
  );

  if (member?.phone?.length) {
    if (!phoneDisplay) {
      phoneDisplay = getPhone({ phone: member?.phone, type: PhoneType.Home });
    }

    if (!cellDisplay) {
      cellDisplay = getPhone({ phone: member?.phone, type: PhoneType.Cell });
    }
  }

  const addressEmpty =
    !state.address.line1 &&
    !state.address.line2 &&
    !state.address.suburb &&
    !state.address.city &&
    !state.address.country &&
    !state.address.postcode;

  const history = useHistory();
  const [dateOfBirth, setDateOfBirth] = React.useState<Date | null | undefined>(
    null
  );
  const [aliases, setAliases] = React.useState<string[]>(member?.aliases);

  const createOption = (label: string) => ({
    label,
    value: label.toLowerCase().replace(/\W/g, ""),
  });

  const handleAddressSelect = (
    _fullAddress: string,
    selectedAddress: AddressMeta | Address
  ) => {
    if ("address_line_1" in selectedAddress) {
      formDispatch({
        type: "set_field",
        payload: {
          field: "address",
          value: {
            line1: selectedAddress.address_line_1,
            line2: selectedAddress.address_line_2 || "",
            suburb: selectedAddress.suburb || "",
            city: selectedAddress.city,
            country: selectedAddress.country,
            postcode: selectedAddress.postcode,
          },
        },
      });
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    userDispatch({ type: "loading", isLoading: true });

    try {
      const personEndpoint = `${WHATA_ENDPOINTS.person()}/${member.id}`;
      const aliasEndpoint = `${personEndpoint}/updateAlias`;
      const changes: Promise<unknown>[] = [];

      const addressUpdate = nullifyObject({
        line1: state.address.line1,
        line2: state.address.line2,
        postcode: state.address.postcode,
        suburb: state.address.suburb,
        city: state.address.city,
        country: state.address.country,
        type: "AH",
        lock_key: addressDisplay?.lastUpdated,
        do_not_contact: false,
      });

      const memberUpdate = nullifyObject({
        ...member,
        title: state.title,
        firstName: state.firstName,
        middleNames: state.middleNames,
        surname: state.surname,
        gender: state.gender,
        occupation: state.occupation,
        dob: dateOfBirth ? format(dateOfBirth, "yyyyMMdd") : member.dob,
      });

      const phoneUpdate = nullifyObject({
        type: PhoneType.Home,
        lock_key: phoneDisplay?.lastUpdated,
        do_not_contact: false,
        value: state.phone,
      });

      const cellUpdate = nullifyObject({
        typeId: PhoneType.Cell,
        lock_key: cellDisplay?.lastUpdated,
        do_not_contact: false,
        value: state.cell,
      });

      // Alias
      if (!deepEqual(aliases, member.aliases)) {
        changes.push(
          whata.post(aliasEndpoint, {
            aliases,
            aliasLastUpdated: member.AliasLastUpdated,
          })
        );
      }

      // Cell
      if (!cellDisplay) {
        if (state.cell) {
          changes.push(whata.post(`${personEndpoint}/phone`, state.cell));
        }
      } else if (!cellReadonly && state.cell !== cellDisplay.value) {
        const cellEndpoint = `${personEndpoint}/phone/${cellDisplay.id}`;

        changes.push(whata.put(cellEndpoint, cellUpdate));
      }

      // Phone
      if (!phoneDisplay) {
        if (state.phone) {
          changes.push(whata.post(`${personEndpoint}/phone`, phoneUpdate));
        }
      } else if (!phoneReadonly && state.phone !== phoneDisplay.value) {
        const phoneEndpoint = `${personEndpoint}/phone/${phoneDisplay.id}`;

        changes.push(whata.put(phoneEndpoint, phoneUpdate));
      }

      // Address
      if (!addressDisplay) {
        if (addressUpdate && !addressEmpty) {
          changes.push(whata.post(`${personEndpoint}/address`, addressUpdate));
        }
      } else if (
        !addressReadonly &&
        !deepEqual(addressUpdate, member.address[0], { ignoreMissing: true })
      ) {
        const addressEndpoint = `${personEndpoint}/address/${member.address[0].id}`;

        changes.push(whata.put(addressEndpoint, addressUpdate));
      }

      if (!deepEqual(member, memberUpdate)) {
        changes.push(whata.put(personEndpoint, memberUpdate));
      }

      await Promise.all(changes);

      fetchUser({
        dispatch: userDispatch,
        history,
        id: member.id,
        location,
      });

      history.push(ROUTES.membersDetails());
    } catch (error) {
      console.error(error);

      userDispatch({
        type: "error",
        error:
          "Auē, there was an error updating your information. Please reload and try again. ",
      });
    } finally {
      userDispatch({ type: "loading", isLoading: false });
    }
  };

  // Remove error messages on mount
  React.useEffect(() => {
    userDispatch({ type: "error", error: null });
  }, []);

  if (isLoading) {
    return (
      <div className="splash">
        <Loader size="large" />
      </div>
    );
  }

  if (!member) {
    return null;
  }

  return (
    <Form onSubmit={handleSubmit} data-testid="update-details">
      <Form.Section heading="Personal Information">
        <Form.Row>
          <Form.InputGroup name="title" label="Title">
            <select
              name="title"
              className="dropdown"
              onChange={(event) =>
                formDispatch({
                  type: "set_field",
                  payload: {
                    field: "title",
                    value: event.currentTarget.value,
                  },
                })
              }
              value={state.title}
            >
              <option disabled>Select an option</option>
              <option value="Ms">Ms</option>
              <option value="Mrs">Mrs</option>
              <option value="Mr">Mr</option>
              <option value="Miss">Miss</option>
            </select>
          </Form.InputGroup>
          <Form.InputGroup />
          <Form.InputGroup />
        </Form.Row>
        <Form.Row>
          <Form.InputGroup name="firstName" label="First name">
            <input
              type="text"
              className="input"
              placeholder="First Name"
              name="firstName"
              value={state.firstName}
              required
              onChange={(event) =>
                formDispatch({
                  type: "set_field",
                  payload: {
                    field: "firstName",
                    value: event.currentTarget.value,
                  },
                })
              }
            />
          </Form.InputGroup>
          <Form.InputGroup name="middleNames" label="Middle names">
            <input
              type="text"
              className="input"
              placeholder="Middle Names"
              name="middleNames"
              value={state.middleNames}
              onChange={(event) =>
                formDispatch({
                  type: "set_field",
                  payload: {
                    field: "middleNames",
                    value: event.currentTarget.value,
                  },
                })
              }
            />
          </Form.InputGroup>
          <Form.InputGroup name="surname" label="Surname">
            <input
              type="text"
              className="input"
              placeholder="Surname"
              name="surname"
              value={state.surname}
              required
              onChange={(event) =>
                formDispatch({
                  type: "set_field",
                  payload: {
                    field: "surname",
                    value: event.currentTarget.value,
                  },
                })
              }
            />
          </Form.InputGroup>
        </Form.Row>

        <Form.Row>
          <Form.InputGroup name="gender" label="Gender">
            <select
              name="gender"
              className="dropdown"
              value={state.gender}
              onChange={(event) =>
                formDispatch({
                  type: "set_field",
                  payload: {
                    field: "gender",
                    value: event.currentTarget.value,
                  },
                })
              }
            >
              <option disabled>Select an option</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </Form.InputGroup>
          <Form.InputGroup name="aliases" label="Aliases">
            <Creatable
              className="select"
              name="aliases"
              value={aliases.map(createOption)}
              onChange={(newValue) =>
                setAliases(newValue.map((value) => value.label))
              }
              isMulti
            />
          </Form.InputGroup>
          <Form.InputGroup name="dob" label="Date of Birth">
            <DatePicker
              defaultDate={parse(state.dob, "yyyymmdd", new Date())}
              onChange={(date) => setDateOfBirth(date)}
            />
          </Form.InputGroup>
        </Form.Row>
      </Form.Section>

      <Form.Section heading="Contact Information">
        <Form.Row>
          <Form.InputGroup name="email" label="Email">
            <input
              type="text"
              className="input"
              placeholder="Email"
              name="email"
              value={state.email}
              readOnly
              disabled
            />
            <Form.Note>
              Email is used for log in, please email us if you need to update
              it.
            </Form.Note>
          </Form.InputGroup>
        </Form.Row>
        <Form.Row>
          <Form.InputGroup name="phone" label="Phone">
            <input
              type="text"
              className="input"
              placeholder="Enter your phone number"
              name="phone"
              value={state.phone as string}
              readOnly={phoneReadonly}
              onChange={(event) =>
                formDispatch({
                  type: "set_field",
                  payload: {
                    field: "phone",
                    value: event.currentTarget.value,
                  },
                })
              }
            />
            {phoneReadonly && (
              <Form.Note>
                Phone is linked to another member, please email us if you need
                to update it.
              </Form.Note>
            )}
          </Form.InputGroup>
          <Form.InputGroup name="cell" label="Cell Phone">
            <input
              type="text"
              className="input"
              placeholder="Enter your cell phone number"
              name="cell"
              value={state.cell as string}
              readOnly={cellReadonly}
              onChange={(event) =>
                formDispatch({
                  type: "set_field",
                  payload: {
                    field: "cell",
                    value: event.currentTarget.value,
                  },
                })
              }
            />
            {cellReadonly && (
              <Form.Note>
                Cell Phone is linked to another member, please email us if you
                need to update it.
              </Form.Note>
            )}
          </Form.InputGroup>
        </Form.Row>
      </Form.Section>

      <Form.Section heading="Address">
        {!addressReadonly && (
          <Form.Row>
            <Form.InputGroup name="addressfinder" label="Search for address">
              <AddressFinderInput
                className="input"
                placeholder="Search for an address in New Zealand..."
                addressfinderkey={process.env.ADDRESS_FINDER_KEY || ""}
                onSelected={handleAddressSelect}
                id="addressFinder"
              />
            </Form.InputGroup>
          </Form.Row>
        )}
        <Form.Row>
          <Form.InputGroup name="line1" label="Line 1">
            <input
              type="text"
              className="input"
              placeholder="Line 1"
              name="line1"
              value={state.address.line1 as string}
              onChange={(event) =>
                formDispatch({
                  type: "set_address",
                  payload: {
                    field: "line1",
                    value: event.currentTarget.value,
                  },
                })
              }
              required={!addressEmpty}
              readOnly={addressReadonly}
            />
          </Form.InputGroup>
          <Form.InputGroup name="line2" label="Line 2">
            <input
              type="text"
              className="input"
              placeholder="Line 2"
              name="line2"
              value={state.address.line2 as string}
              onChange={(event) =>
                formDispatch({
                  type: "set_address",
                  payload: {
                    field: "line2",
                    value: event.currentTarget.value,
                  },
                })
              }
              readOnly={addressReadonly}
            />
          </Form.InputGroup>
          <Form.InputGroup name="suburb" label="Suburb">
            <input
              type="text"
              className="input"
              placeholder="Suburb"
              name="suburb"
              value={state.address.suburb as string}
              onChange={(event) =>
                formDispatch({
                  type: "set_address",
                  payload: {
                    field: "suburb",
                    value: event.currentTarget.value,
                  },
                })
              }
              readOnly={addressReadonly}
            />
          </Form.InputGroup>
        </Form.Row>
        <Form.Row>
          <Form.InputGroup name="city" label="City">
            <input
              type="text"
              className="input"
              placeholder="City"
              name="city"
              value={state.address.city as string}
              onChange={(event) =>
                formDispatch({
                  type: "set_address",
                  payload: {
                    field: "city",
                    value: event.currentTarget.value,
                  },
                })
              }
              required={!addressEmpty}
              readOnly={addressReadonly}
            />
          </Form.InputGroup>
          <Form.InputGroup name="country" label="Country">
            <input
              type="text"
              className="input"
              placeholder="Country"
              name="country"
              value={state.address.country as string}
              onChange={(event) =>
                formDispatch({
                  type: "set_address",
                  payload: {
                    field: "country",
                    value: event.currentTarget.value,
                  },
                })
              }
              required={!addressEmpty}
              readOnly={addressReadonly}
            />
          </Form.InputGroup>
          <Form.InputGroup name="postcode" label="Postcode">
            <input
              type="text"
              className="input"
              placeholder="Postcode"
              name="postcode"
              value={state.address.postcode as string}
              onChange={(event) =>
                formDispatch({
                  type: "set_address",
                  payload: {
                    field: "postcode",
                    value: event.currentTarget.value,
                  },
                })
              }
              required={!addressEmpty}
              readOnly={addressReadonly}
            />
          </Form.InputGroup>
        </Form.Row>
        {addressReadonly && (
          <Form.Row>
            <Form.Note>
              Address is linked to another member, please email us if you need
              to update it.
            </Form.Note>
          </Form.Row>
        )}
      </Form.Section>

      <Form.Section heading="Other information">
        <Form.Row>
          <Form.InputGroup name="occupation" label="Occupation">
            <input
              type="text"
              className="input"
              placeholder="Enter your occupation"
              name="occupation"
              defaultValue={member.occupation}
              onChange={(event) =>
                formDispatch({
                  type: "set_field",
                  payload: {
                    field: "occupation",
                    value: event.currentTarget.value,
                  },
                })
              }
            />
          </Form.InputGroup>
          <Form.InputGroup />
          <Form.InputGroup />
        </Form.Row>
      </Form.Section>
      <Form.Actions>
        <Button className="button-primary" submit>
          Update
        </Button>
        <Link
          to={ROUTES.membersDetails()}
          className="button tertiary"
          type="button"
        >
          Cancel
        </Link>
      </Form.Actions>
    </Form>
  );
};
